import { type ReactNode, useCallback, useEffect, useState } from "react";

import {
  Button,
  CheckIcon,
  ChevronLeftIcon,
  CloseIcon,
  Column,
  DeleteIcon,
  IconButton,
  Menu,
  MenuActionsButton,
  MenuItem,
  MenuList,
  ResetIcon,
  Row,
  Text,
  Tooltip,
  UndoIcon,
} from "@hightouch/htui";

import type { MentionNames } from "@hightouch/lib/llm/asset-comments/mention-tokens";
import { AssetCommentStatuses } from "@hightouch/lib/llm/asset-comments/types";
import { textShimmer } from "src/components/llm/components/loading";
import { useScrollFade } from "src/hooks/use-scroll-fade";

import { CommentComposer, type CommentDraft } from "./comment-composer";
import {
  CommentItem,
  LINKED_COMMENT_ATTRIBUTE,
  type CommentMenu,
  type CommentAuthor,
  type CommentItemProps,
} from "./comment-item";
import type { CommentDensity } from "./markdown-comment-body";
import { UnreadDot } from "./unread-dot";
import type { MentionOption } from "./mention-options-list";
import { useOwnWheel } from "./use-own-wheel";
import { useThreadCollapse } from "./use-thread-collapse";
import { useScrollToNewest } from "./use-scroll-to-newest";

const INLINE_CARD_WIDTH = 320;

/**
 * How tall the anchored card may get before its conversation scrolls.
 *
 * The cap is on the card rather than on the comment list, so the header and the
 * reply box keep their space and only the conversation between them gives.
 */
const INLINE_CARD_MAX_HEIGHT = 420;

/** Replies start under the opening comment's name, clearing its 20px avatar and gap. */
const COMPACT_REPLY_INDENT = 7;

// A compact comment's text starts after its 8px padding, 24px avatar and 8px gap.
const COMPACT_BODY_INDENT = 10;

export type ThreadComment = {
  id: string;
  author: CommentAuthor;
  body: string | null;
  mentionNames: MentionNames;
  createdAt: string;
  status: string;
  canModify: boolean;
  /** The run filling in a pending agent reply; null on everything else. */
  interactionId: string | null;
  /** The run's chat session, for the staff-only link to it. */
  sessionId: string | null;
  badge?: ReactNode;
};

type Props = {
  threadId: string;
  comments: ThreadComment[];
  isResolved: boolean;
  isSelected: boolean;
  /**
   * `rail` is the sidebar summary — the opening comment and a reply count,
   * nothing else. `inline` is the card anchored to the pin on the canvas: the
   * whole thread, and the only place replying happens.
   */
  variant?: "rail" | "inline";
  /**
   * Inline and embedded only. Compact drops the header, puts resolve on the
   * opening comment, and folds the reply box behind a "Write reply" button.
   */
  density?: CommentDensity;
  /** The thread's anchor time, so only its opening comment shows it. */
  timestampLabel?: string;
  /** Identifies the content this thread refers to. */
  subtitle?: string | null;
  searchMentions: (query: string) => Promise<MentionOption[]>;
  onSelect: () => void;
  onReply: (draft: CommentDraft) => void;
  onToggleResolved: () => void;
  /** Whether the viewer may resolve or reopen the thread. */
  canResolve?: boolean;
  /** Inline and compact only. The opening comment's menu, when it stands for more than itself. */
  openingMenu?: CommentMenu;
  /** Inline only. Sits inside the reply box, such as a choice about what replying also does. */
  replyOption?: ReactNode;
  /**
   * Deletes a single reply — inline card only. The rail renders just the
   * opening comment, where the only delete is the whole thread.
   */
  onDeleteComment?: (commentId: string) => void;
  /**
   * Saves a rewritten comment — inline card only, like {@link onDeleteComment}.
   * Resolves to whether it saved: the box stays open when it didn't.
   */
  onEditComment?: (commentId: string, draft: CommentDraft) => Promise<boolean>;
  isSavingEdit?: boolean;
  /** Deletes the whole thread. The rail's only delete, and the inline header's. */
  onDeleteThread: () => void;
  /** Whether the viewer authored the opening comment, so may delete the thread. */
  canDeleteThread?: boolean;
  /** Inline only — dismisses the card without leaving comment mode. */
  onClose?: () => void;
  /** Inline only. Sits inside another surface rather than over a canvas, so it fills it, has no frame of its own, and has nothing to close. */
  isEmbedded?: boolean;
  /**
   * Embedded only. Caps the card at the floating card's height, for a surface
   * that sizes to its content, like a popover, where there is no height to fill.
   * Unset, the card fills its surface.
   */
  capHeight?: boolean;
  /** Inline only. Goes back to the list the card was opened from. */
  onBack?: () => void;
  isReplying?: boolean;
  /** Whether the viewer has unread comments here. */
  isUnread?: boolean;
  /** Marks read or unread by hand. Opening the thread already marks it read. */
  onToggleRead?: () => void;
  /** Pointed at from elsewhere — for ads, its pin on the canvas. Rail only. */
  isHighlighted?: boolean;
  /** The comment a link points at, shown with a soft background and never folded away. */
  highlightedCommentId?: string | null;
  /** Copies a link to one comment. The comment menus offer it only when this is set. */
  onCopyCommentLink?: (commentId: string) => void;
  onHover?: (isHovered: boolean) => void;
  /** Off where the surface replies through a box of its own, so the card offers no second one. */
  canReply?: boolean;
};

export const CommentThreadCard = ({
  threadId,
  comments,
  isResolved,
  isSelected,
  variant = "rail",
  density = "default",
  timestampLabel,
  subtitle,
  searchMentions,
  onSelect,
  onReply,
  onToggleResolved,
  canResolve = true,
  openingMenu,
  replyOption,
  onDeleteComment,
  onEditComment,
  isSavingEdit,
  onDeleteThread,
  canDeleteThread = false,
  onClose,
  isEmbedded = false,
  capHeight = false,
  onBack,
  isReplying,
  isUnread = false,
  onToggleRead,
  isHighlighted = false,
  highlightedCommentId = null,
  onCopyCommentLink,
  onHover,
  canReply = true,
}: Props) => {
  // Before the early return: hooks can't run conditionally.
  const conversationRef = useOwnWheel<HTMLDivElement>();
  const scrollFade = useScrollFade();
  const attachConversation = useCallback(
    (el: HTMLDivElement | null) => {
      conversationRef.current = el;
      scrollFade.ref(el);
    },
    [conversationRef, scrollFade.ref],
  );
  const [isExpanded, setIsExpanded] = useState(false);
  // Stays open once asked for, so a reply being typed survives the next poll.
  const [isReplyOpen, setIsReplyOpen] = useState(false);

  // Deciding rows are agents reading the thread, not comments: they carry no
  // body and may never become one, so they raise the footer line rather than
  // being rendered in the conversation.
  const isAnyoneDeciding = comments.some(
    (comment) => comment.status === AssetCommentStatuses.Deciding,
  );
  const posted = comments.filter(
    (comment) => comment.status !== AssetCommentStatuses.Deciding,
  );

  const [firstComment, ...replies] = posted;

  // Decided once per visit and held outside this component, because the
  // anchored card remounts when a reply lands. See `useThreadCollapse`.
  const isCollapsible = useThreadCollapse({
    threadId,
    replyCount: Math.max(posted.length - 1, 0),
    isEnabled: variant === "inline",
  });

  // Only the anchored card scrolls its own conversation; the rail shows the
  // opening comment and has nothing to bring into view.
  useScrollToNewest({
    threadId,
    containerRef: conversationRef,
    newestId: variant === "inline" ? posted[posted.length - 1]?.id : undefined,
  });

  // A long thread collapses to its two ends: the opening comment says what this
  // is about and the newest reply is where it got to, and the middle is the
  // part a reader returning to a conversation already knows. Expanding is
  // one-way — having asked for the whole thread, collapsing it again while
  // reading would move the text out from under them.
  const hiddenCount = Math.max(replies.length - 1, 0);

  // Split so the newest reply keeps its identity across the expand: it renders
  // in the same place before and after, rather than remounting as part of a
  // longer list.
  const middleReplies = isCollapsible ? replies.slice(0, -1) : [];
  const trailingReplies = isCollapsible ? replies.slice(-1) : replies;
  // A linked comment in the middle keeps the thread open, so the link lands on it.
  const isCollapsed =
    !isExpanded &&
    isCollapsible &&
    !middleReplies.some((reply) => reply.id === highlightedCommentId);

  // The conversation scrolls within the card's cap, so a link to a later reply brings it up into view.
  useEffect(() => {
    if (!highlightedCommentId) return;
    const container = conversationRef.current;
    const linked = container?.querySelector<HTMLElement>(
      `[${LINKED_COMMENT_ATTRIBUTE}]`,
    );
    if (!container || !linked) return;
    const offset =
      linked.getBoundingClientRect().top -
      container.getBoundingClientRect().top;
    if (offset < 0 || offset + linked.offsetHeight > container.clientHeight)
      container.scrollTop += offset;
  }, [conversationRef, highlightedCommentId]);

  const commentLink = (commentId: string) => ({
    isHighlighted: commentId === highlightedCommentId,
    onCopyLink: onCopyCommentLink && (() => onCopyCommentLink(commentId)),
  });

  const editFor = (commentId: string): CommentItemProps["edit"] =>
    onEditComment && {
      searchMentions,
      onSave: (draft) => onEditComment(commentId, draft),
      isSaving: isSavingEdit,
    };

  if (!firstComment) {
    return null;
  }

  const toReplyItem = (reply: ThreadComment) => (
    <CommentItem
      {...commentLink(reply.id)}
      key={reply.id}
      author={reply.author}
      body={reply.body}
      mentionNames={reply.mentionNames}
      createdAt={reply.createdAt}
      status={reply.status}
      interactionId={reply.interactionId}
      sessionId={reply.sessionId}
      badge={reply.badge}
      canModify={reply.canModify}
      paddingX={0}
      density="compact"
      onDelete={() => onDeleteComment?.(reply.id)}
      edit={editFor(reply.id)}
    />
  );

  if (variant === "inline" && density === "compact") {
    // With replies under it, deleting the opening comment would orphan them, so it deletes the thread.
    const deletesThread = canDeleteThread && replies.length > 0;

    return (
      <Column>
        <CommentItem
          {...commentLink(firstComment.id)}
          key={firstComment.id}
          author={firstComment.author}
          body={firstComment.body}
          mentionNames={firstComment.mentionNames}
          createdAt={firstComment.createdAt}
          status={firstComment.status}
          interactionId={firstComment.interactionId}
          sessionId={firstComment.sessionId}
          badge={firstComment.badge}
          timestampLabel={timestampLabel}
          subtitle={subtitle}
          canModify={firstComment.canModify}
          paddingX={0}
          density="compact"
          isResolved={isResolved}
          onResolve={canResolve ? onToggleResolved : undefined}
          isUnread={isUnread}
          onToggleRead={onToggleRead}
          onDelete={
            deletesThread
              ? onDeleteThread
              : () => onDeleteComment?.(firstComment.id)
          }
          deleteLabel={deletesThread ? "Delete thread" : undefined}
          edit={editFor(firstComment.id)}
          menu={openingMenu}
        />

        <Column pl={COMPACT_REPLY_INDENT}>
          {isCollapsed && (
            <Row py={1}>
              <Button
                size="sm"
                variant="link"
                onClick={() => setIsExpanded(true)}
              >
                {`Show ${hiddenCount} ${hiddenCount === 1 ? "reply" : "replies"}`}
              </Button>
            </Row>
          )}

          {!isCollapsed && middleReplies.map(toReplyItem)}
          {trailingReplies.map(toReplyItem)}

          {isAnyoneDeciding && (
            <Row py={1}>
              <Text size="sm" {...textShimmer}>
                Reading…
              </Text>
            </Row>
          )}
        </Column>

        {/* Under the opening comment's text, which sits past its avatar. */}
        {canReply && (
          <Column pl={COMPACT_BODY_INDENT}>
            {isReplyOpen ? (
              <Column pt={1}>
                <CommentComposer
                  placeholder="Reply…"
                  searchMentions={searchMentions}
                  onSubmit={onReply}
                  isSubmitting={isReplying}
                  borderRadius="xl"
                  autoFocus
                  option={replyOption}
                />
              </Column>
            ) : (
              <Row>
                <Button
                  size="sm"
                  variant="tertiary"
                  icon={UndoIcon}
                  onClick={() => setIsReplyOpen(true)}
                >
                  Reply
                </Button>
              </Row>
            )}
          </Column>
        )}
      </Column>
    );
  }

  return variant === "inline" ? (
    <Column
      width={isEmbedded ? "100%" : `${INLINE_CARD_WIDTH}px`}
      // The card owns its own height cap. Its scrolling middle uses `flex={1}`,
      // which needs a definite height to resolve against — and the pin anchors
      // it in an `align="flex-start"` row, where a child sizes to its content.
      // Without this the card grows to fit the whole conversation and the inner
      // `overflowY` never has anything to scroll.
      maxHeight={
        !isEmbedded || capHeight ? `${INLINE_CARD_MAX_HEIGHT}px` : undefined
      }
      height={isEmbedded ? "100%" : undefined}
      bg="white"
      borderRadius={isEmbedded ? undefined : "4xl"}
      boxShadow={isEmbedded ? undefined : "lg"}
      borderWidth={isEmbedded ? undefined : "hairline"}
      borderStyle="solid"
      borderColor="base.border"
      cursor="auto"
      onClick={(event) => event.stopPropagation()}
    >
      {/* Pinned above the thread so resolve and close stay reachable however
          far the conversation is scrolled. */}
      <Row
        align="center"
        justify="space-between"
        gap={2}
        px={4}
        pt={3}
        pb={1}
        flexShrink={0}
      >
        {/* Beside the label rather than among the buttons on the right, where
            a dot reads as another control. */}
        <Row align="center" gap={1.5} minWidth={0}>
          {onBack && (
            <IconButton
              aria-label="Back to comments"
              icon={ChevronLeftIcon}
              size="sm"
              variant="tertiary"
              onClick={onBack}
            />
          )}
          <Text size="sm" fontWeight="medium" color="text.primary">
            Comment
          </Text>

          {isUnread && <UnreadDot />}
        </Row>

        <Row align="center" gap={0.5}>
          {(canDeleteThread || onToggleRead) && (
            // Fixed: inside a popover that follows a pin, an absolute hidden list can make the page scroll.
            <Menu computePositionOnMount={false} strategy="fixed">
              <MenuActionsButton size="sm" />
              <MenuList>
                {onToggleRead && (
                  <MenuItem onClick={onToggleRead}>
                    {isUnread ? "Mark as read" : "Mark as unread"}
                  </MenuItem>
                )}
                {canDeleteThread && (
                  <MenuItem
                    icon={DeleteIcon}
                    variant="danger"
                    onClick={onDeleteThread}
                  >
                    Delete thread
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}

          {/* The icon shows what the button does, not the current state: a
              check to resolve, an undo to put it back. */}
          {canResolve && (
            <Tooltip message={isResolved ? "Reopen" : "Mark as resolved"}>
              <IconButton
                aria-label={isResolved ? "Reopen thread" : "Mark as resolved"}
                icon={isResolved ? ResetIcon : CheckIcon}
                size="sm"
                variant="tertiary"
                onClick={onToggleResolved}
              />
            </Tooltip>
          )}

          {!isEmbedded && (
            <IconButton
              aria-label="Close thread"
              icon={CloseIcon}
              size="sm"
              variant="tertiary"
              onClick={() => onClose?.()}
            />
          )}
        </Row>
      </Row>

      {/* `minHeight={0}` is what lets this shrink below its content: a flex
          item's default floor is its content size, which would push the card
          past its cap instead of scrolling. The ref keeps the trackpad from
          panning the canvas underneath instead of scrolling the thread, and
          fades the conversation at whichever edge it is clipped against. */}
      <Column
        ref={attachConversation}
        flex={1}
        minHeight={0}
        overflowY="auto"
        sx={scrollFade.sx}
      >
        <CommentItem
          {...commentLink(firstComment.id)}
          // Keyed like the replies, so an open edit box can't outlive the
          // comment it was opened on.
          key={firstComment.id}
          author={firstComment.author}
          body={firstComment.body}
          mentionNames={firstComment.mentionNames}
          createdAt={firstComment.createdAt}
          status={firstComment.status}
          interactionId={firstComment.interactionId}
          sessionId={firstComment.sessionId}
          badge={firstComment.badge}
          timestampLabel={timestampLabel}
          canModify={firstComment.canModify}
          paddingX={4}
          subtitle={subtitle}
          onDelete={() => onDeleteComment?.(firstComment.id)}
          edit={editFor(firstComment.id)}
        />

        {isCollapsed && (
          // Aligned with the comment text above and below it, so the thread
          // reads as one column rather than the control interrupting it.
          <Row px={4} py={1}>
            {/* A button, not a link: this reveals the rest of the thread in
                place rather than navigating anywhere. */}
            <Button
              size="sm"
              variant="link"
              onClick={() => setIsExpanded(true)}
            >
              {`Show ${hiddenCount} ${hiddenCount === 1 ? "reply" : "replies"}`}
            </Button>
          </Row>
        )}

        {!isCollapsed &&
          middleReplies.map((reply) => (
            <CommentItem
              {...commentLink(reply.id)}
              key={reply.id}
              author={reply.author}
              body={reply.body}
              mentionNames={reply.mentionNames}
              createdAt={reply.createdAt}
              status={reply.status}
              interactionId={reply.interactionId}
              sessionId={reply.sessionId}
              badge={reply.badge}
              canModify={reply.canModify}
              paddingX={4}
              onDelete={() => onDeleteComment?.(reply.id)}
              edit={editFor(reply.id)}
            />
          ))}

        {trailingReplies.map((reply) => (
          <CommentItem
            {...commentLink(reply.id)}
            key={reply.id}
            author={reply.author}
            body={reply.body}
            mentionNames={reply.mentionNames}
            createdAt={reply.createdAt}
            status={reply.status}
            interactionId={reply.interactionId}
            sessionId={reply.sessionId}
            badge={reply.badge}
            canModify={reply.canModify}
            paddingX={4}
            onDelete={() => onDeleteComment?.(reply.id)}
            edit={editFor(reply.id)}
          />
        ))}
      </Column>

      {isAnyoneDeciding && (
        // One line for the thread however many agents are reading it: what the
        // reader needs to know is that something is happening, not how much.
        <Row px={4} pt={1} flexShrink={0}>
          <Text size="sm" {...textShimmer}>
            Reading…
          </Text>
        </Row>
      )}

      {canReply && (
        <Column px={4} pb={4} pt={1} flexShrink={0}>
          <CommentComposer
            placeholder="Reply…"
            searchMentions={searchMentions}
            onSubmit={onReply}
            isSubmitting={isReplying}
            borderRadius="xl"
            option={replyOption}
          />
        </Column>
      )}
    </Column>
  ) : (
    <Column
      borderRadius="lg"
      // Same background as hovering it directly — one is just triggered from
      // the pin.
      bg={
        isSelected
          ? "primary.background"
          : isHighlighted
            ? "base.background"
            : "transparent"
      }
      cursor="pointer"
      _hover={{ bg: isSelected ? "primary.background" : "base.background" }}
      transitionProperty="background-color"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      onClick={onSelect}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <CommentItem
        {...commentLink(firstComment.id)}
        isUnread={isUnread}
        // The rail has no header, so the read toggle lives in the comment's
        // menu; the inline card has its own.
        onToggleRead={onToggleRead}
        author={firstComment.author}
        body={firstComment.body}
        mentionNames={firstComment.mentionNames}
        createdAt={firstComment.createdAt}
        status={firstComment.status}
        interactionId={firstComment.interactionId}
        sessionId={firstComment.sessionId}
        badge={firstComment.badge}
        timestampLabel={timestampLabel}
        subtitle={subtitle}
        canModify={firstComment.canModify}
        isResolved={isResolved}
        onResolve={canResolve ? onToggleResolved : undefined}
        onDelete={onDeleteThread}
        deleteLabel="Delete thread"
      />

      {/* A count, not a control: opening the thread happens by selecting the
          card, which shows it inline on the canvas. */}
      {replies.length > 0 && (
        <Row px={3} pb={3}>
          <Text size="sm" color="text.accent">
            {replies.length} {replies.length === 1 ? "reply" : "replies"}
          </Text>
        </Row>
      )}
    </Column>
  );
};
